import {
  Chip,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  actionColumn,
  collapseColumn,
  column,
  dateColumn,
  emptyCell,
  useModal,
} from "@homebound/beam";
import { dateCell } from "src/components/gridTableCells";
import { BidPackageBidderKind } from "src/generated/graphqlTypes";
import {
  BidPackageDetailPageDocument,
  PageBidPackageDetailFragment,
  useSaveBidPackageRequestMutation,
} from "src/routes/bids/BidPackageDetailPage.generated";
import {
  BiddersTab_BidderFragment,
  BiddersTab_TradePartnerContactFragment,
} from "src/routes/bids/components/BiddersTab.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { useSaveBidContractMutation } from "src/routes/developments/procurement/DevelopmentContractPage.generated";
import { createTradePartnerUrl } from "src/RouteUrls";
import { isDefined } from "src/utils";

type BiddersTabProps = { bidPackage: PageBidPackageDetailFragment };

export function BiddersTab(props: BiddersTabProps) {
  const { bidPackage } = props;
  const { openModal } = useModal();
  const [saveBidPackageRequest] = useSaveBidPackageRequestMutation();
  const [saveBidContract] = useSaveBidContractMutation({ refetchQueries: [BidPackageDetailPageDocument] });
  const rows = createRows(bidPackage.bidders);

  function onArchive(archive: boolean, bidder: BiddersTab_BidderFragment) {
    const isInternalEstimate = bidder.kind === BidPackageBidderKind.InternalEstimate;
    openModal({
      content: (
        <ConfirmationModal
          confirmationMessage={
            <>
              Are you sure you want to {archive ? "archive" : "unarchive"} Trade <b>{bidder.name}</b>?{" "}
              {archive
                ? "You can unarchive them later if needed."
                : "They will be an active bidder on future versions."}
            </>
          }
          onConfirmAction={() =>
            isInternalEstimate
              ? saveBidContract({ variables: { input: { id: bidder.bidContract!.id, archive } } })
              : saveBidPackageRequest({ variables: { input: { id: bidder.request!.id, archive } } })
          }
          title={archive ? "Archive Trade" : "Unarchive Trade"}
          label={archive ? "Archive" : "Unarchive"}
        />
      ),
    });
  }

  return <GridTable columns={createColumns(onArchive)} rows={rows} />;
}

function createRows(bidders: BiddersTab_BidderFragment[]): GridDataRow<Row>[] {
  return [
    { kind: "header" as const, id: "header", data: undefined },
    ...bidders.map((bidder) => {
      const secondary = bidder.request?.secondaryContact;
      return {
        kind: "bidder" as const,
        id: bidder.id,
        data: { bidder },
        children: secondary
          ? [{ kind: "bidderSecondary" as const, id: secondary.id, data: { bidder, secondary } }]
          : undefined,
      };
    }),
  ];
}

type HeaderRow = { kind: "header"; data: undefined };
type BidderRow = { kind: "bidder"; data: { bidder: BiddersTab_BidderFragment } };
type BidderSecondaryRow = {
  kind: "bidderSecondary";
  data: { bidder: BiddersTab_BidderFragment; secondary: BiddersTab_TradePartnerContactFragment };
};

type Row = HeaderRow | BidderRow | BidderSecondaryRow;

function createColumns(onArchive: (archive: boolean, bidder: BiddersTab_BidderFragment) => void): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({
      bidder: (b) => ({ ...emptyCell, css: applyStyling(b.bidder) }),
      bidderSecondary: (b) => ({ ...emptyCell, css: applyStyling(b.bidder) }),
    }),
    column<Row>({
      id: "companyName",
      header: "Company Name",
      bidder: (b) => ({ content: b.bidder.name, css: applyStyling(b.bidder) }),
      bidderSecondary: (b) => ({ ...emptyCell, css: applyStyling(b.bidder) }),
    }),
    column<Row>({
      id: "contact",
      header: "Contact",
      bidder: (b) => {
        const primary = b.bidder.request?.primaryContact;
        if (!primary) return { ...emptyCell, css: applyStyling(b.bidder) };
        return {
          content: (
            <>
              {primary.name}{" "}
              <Chip
                text={
                  <span css={Css.if(isDefined(b.bidder.request?.archivedAt)).color(Palette.Gray500).$}>Primary</span>
                }
                type="neutral"
                xss={Css.ml1.$}
              />
            </>
          ),
          css: applyStyling(b.bidder),
        };
      },
      bidderSecondary: (b) => ({
        content: (
          <>
            {b.secondary.name}{" "}
            <Chip
              text={
                <span css={Css.if(isDefined(b.bidder.request?.archivedAt)).color(Palette.Gray500).$}>Secondary</span>
              }
              type="neutral"
              xss={Css.ml1.$}
            />
          </>
        ),
        css: applyStyling(b.bidder),
      }),
    }),
    column<Row>({
      id: "phone",
      header: "Phone",
      bidder: (b) => ({ content: b.bidder.request?.primaryContact?.mobilePhone, css: applyStyling(b.bidder) }),
      bidderSecondary: (b) => ({ content: b.secondary.mobilePhone, css: applyStyling(b.bidder) }),
    }),
    column<Row>({
      id: "email",
      header: "Email",
      bidder: (b) => ({ content: b.bidder.request?.primaryContact?.email, css: applyStyling(b.bidder) }),
      bidderSecondary: (b) => ({ content: b.secondary.email, css: applyStyling(b.bidder) }),
    }),
    dateColumn<Row>({
      w: "110px",
      id: "sent",
      header: "Invitation Sent",
      bidder: (b) => ({ ...dateCell(b.bidder.request?.sentDate), css: applyStyling(b.bidder) }),
      bidderSecondary: (b) => ({ ...emptyCell, css: applyStyling(b.bidder) }),
    }),
    actionColumn<Row>({
      header: emptyCell,
      bidder: (b) => {
        const isInternalEstimate = b.bidder.kind === BidPackageBidderKind.InternalEstimate;
        const archived = isDefined(b.bidder.archivedAt);
        return {
          content: (
            <div css={Css.df.gap1.$}>
              {archived ? (
                <IconButton inc={2} icon="undoCircle" onClick={() => onArchive(false, b.bidder)} />
              ) : (
                <>
                  {!isInternalEstimate && (
                    <IconButton
                      inc={2}
                      icon="linkExternal"
                      openInNew
                      tooltip="View Trade Partner"
                      onClick={createTradePartnerUrl(b.bidder.tradePartner!.id)}
                    />
                  )}
                  <IconButton inc={2} icon="archive" onClick={() => onArchive(true, b.bidder)} />
                </>
              )}
            </div>
          ),
          css: applyStyling(b.bidder),
        };
      },
      bidderSecondary: (b) => ({ ...emptyCell, css: applyStyling(b.bidder) }),
      w: "120px",
    }),
  ];
}

function applyStyling(bidder: BiddersTab_BidderFragment) {
  if (isDefined(bidder.archivedAt)) {
    return Css.bgGray50.color(Palette.Gray500).$;
  }
}
